import { Controller } from "@hotwired/stimulus"
import { prepare, layout } from '@chenglou/pretext'
import { getFontFromStyles, parseLineHeight, onResize } from "../helpers/pretext"

// Enhanced collapsible panel with pretext-powered smooth height animations
// Avoids layout thrashing by calculating text height without DOM measurements
export default class extends Controller {
  static targets = ["content", "icon", "inner", "text"]
  static values = {
    expanded: { type: Boolean, default: false },
    duration: { type: Number, default: 200 },
    measure: { type: Boolean, default: false },
    persistKey: { type: String, default: "" }
  }

  preparedText = null
  fullHeight = 0
  cleanupResize = null
  isAnimating = false

  connect() {
    if (this.persistKeyValue) {
      const saved = localStorage.getItem(this.persistKeyValue)
      if (saved !== null) {
        this.expandedValue = saved === "true"
      }
    }

    if (this.measureValue && this.hasTextTarget) {
      this.prepareText()
    }
    this.updateState()

    // Debounced resize handler
    this.cleanupResize = onResize(() => {
      if (this.measureValue && this.hasTextTarget) {
        this.prepareText()
        if (this.expandedValue) {
          this.updateHeight(true)
        }
      }
    }, 100)
  }

  disconnect() {
    if (this.cleanupResize) {
      this.cleanupResize()
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.expandedValue = !this.expandedValue
    if (this.persistKeyValue) {
      localStorage.setItem(this.persistKeyValue, this.expandedValue)
    }
    this.updateState()
  }

  prepareText() {
    if (!this.hasTextTarget) return

    const styles = getComputedStyle(this.textTarget)
    const font = getFontFromStyles(styles)
    const text = this.textTarget.textContent || ''

    this.preparedText = prepare(text, font)
    this.calculateHeight()
  }

  calculateHeight() {
    if (!this.hasTextTarget || !this.hasInnerTarget || !this.preparedText) return

    const textStyles = getComputedStyle(this.textTarget)
    const innerStyles = getComputedStyle(this.innerTarget)

    const lineHeight = parseLineHeight(textStyles)
    const contentWidth = this.textTarget.clientWidth
    const paddingY = parseFloat(innerStyles.paddingTop) + parseFloat(innerStyles.paddingBottom)

    const { height } = layout(this.preparedText, contentWidth, lineHeight)
    this.fullHeight = Math.ceil(height + paddingY)
  }

  updateState() {
    if (this.expandedValue) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  expand() {
    if (!this.hasContentTarget) return

    if (this.measureValue && this.hasInnerTarget) {
      this.updateHeight(true)
    } else {
      this.contentTarget.classList.remove("hidden")
    }

    this.updateIcon()
  }

  collapse() {
    if (!this.hasContentTarget) return

    if (this.measureValue && this.hasInnerTarget) {
      this.updateHeight(false)
    } else {
      this.contentTarget.classList.add("hidden")
    }

    this.updateIcon()
  }

  updateHeight(expanded) {
    if (!this.hasContentTarget) return
    if (this.isAnimating) return

    const content = this.contentTarget

    if (expanded) {
      // Measure before expanding
      if (this.fullHeight === 0) {
        this.calculateHeight()
      }

      // Start from 0
      content.style.height = '0px'
      content.classList.remove("hidden")
      content.style.overflow = 'hidden'

      // Force reflow
      content.offsetHeight

      // Animate to full height
      this.isAnimating = true
      content.style.transition = `height ${this.durationValue}ms ease-out`
      content.style.height = `${this.fullHeight}px`

      setTimeout(() => {
        content.style.height = ''
        content.style.overflow = ''
        content.style.transition = ''
        this.isAnimating = false
      }, this.durationValue)
    } else {
      // Collapse
      content.style.overflow = 'hidden'
      content.style.height = `${content.scrollHeight}px`

      // Force reflow
      content.offsetHeight

      this.isAnimating = true
      content.style.transition = `height ${this.durationValue}ms ease-in`
      content.style.height = '0px'

      setTimeout(() => {
        content.classList.add("hidden")
        content.style.height = ''
        content.style.overflow = ''
        content.style.transition = ''
        this.isAnimating = false
      }, this.durationValue)
    }
  }

  updateIcon() {
    if (!this.hasIconTarget) return

    const iconSvg = this.iconTarget.querySelector("svg")
    if (!iconSvg) return

    const pathElement = iconSvg.querySelector("path")
    if (!pathElement) return

    const chevronDownPath = "M6 9L12 15L18 9"
    const chevronUpPath = "M18 15L12 9L6 15"
    const newPath = this.expandedValue ? chevronUpPath : chevronDownPath

    pathElement.setAttribute("d", newPath)

    // Rotate animation
    iconSvg.style.transition = `transform ${this.durationValue}ms ease`
    iconSvg.style.transform = this.expandedValue ? 'rotate(180deg)' : 'rotate(0deg)'
  }
}
